import {
  WIZARD_STATUS as PLATFORM_WIZARD_STATUS,
  WIZARD_STATUS_RESTARTED,
  restartShouldRedirect,
} from 'platform/site-wide/wizard';
import { resetScreenerSession } from '../subtask/screenerSession';

import {
  FORM_STATUS_BDD,
  SAVED_SEPARATION_DATE,
  START_PATH,
  WIZARD_STATUS,
} from '../constants';

// This app's nested routes do not always expose its wizard storage key to
// FormStartControls, so a restart may be recorded under either key.
const WIZARD_STATUS_KEYS = [WIZARD_STATUS, PLATFORM_WIZARD_STATUS];

// Checking a key also changes "restarting" to "restarted". Check every key
// before deciding whether a restart was found so no stale status remains.
const isRestartPending = () =>
  WIZARD_STATUS_KEYS.map(key => restartShouldRedirect(key)).some(Boolean);

const resetRestartSession = () => {
  try {
    // A stale "complete" status hides the start link on the introduction page.
    sessionStorage.setItem(WIZARD_STATUS, WIZARD_STATUS_RESTARTED);
    // Do not carry the abandoned application's BDD answers into the new one.
    sessionStorage.removeItem(FORM_STATUS_BDD);
    sessionStorage.removeItem(SAVED_SEPARATION_DATE);
    // The new application's screener must start at the first question.
    resetScreenerSession();
  } catch (error) {
    // Cleanup is best-effort; navigation can continue to the chosen route.
  }
};

// Navigating can synchronously call onFormLoaded again before its load state
// settles. Keep the destination through the current task so that second call
// cannot resume at the abandoned application's returnUrl.
let pendingDestination = null;

export const resetRestartRoutingState = () => {
  pendingDestination = null;
};

/**
 * Shared restart destination rule for SaveInProgress callbacks and onFormLoaded.
 *
 * @param {object} [options]
 * @param {boolean} [options.newScreenerQuestionFlow=false]
 * @returns {string}
 */
export const getRestartDestination = ({
  newScreenerQuestionFlow = false,
} = {}) => (newScreenerQuestionFlow ? '/introduction' : START_PATH);

/**
 * Redux `isStartingOver` is not cleared when a restart finishes, so the wizard
 * status is also required to identify a new restart. Returns the configured
 * destination while restart navigation is in progress, and null otherwise.
 *
 * @param {object} props
 * @param {boolean} props.isStartingOver - save-in-progress restart flag
 * @param {string} props.destination - restart destination path
 * @returns {string|null} route path, or null to route normally
 */
export const prepareRestartRedirect = ({
  isStartingOver = false,
  destination = START_PATH,
} = {}) => {
  if (!isStartingOver) {
    return null;
  }

  if (pendingDestination) {
    return pendingDestination;
  }

  let restartPending = false;
  try {
    restartPending = isRestartPending();
  } catch (error) {
    return null; // storage access blocked
  }
  if (!restartPending) {
    return null;
  }

  resetRestartSession();

  pendingDestination = destination;
  setTimeout(resetRestartRoutingState, 0);

  return destination;
};
